import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Calendar, 
  MapPin, 
  Package, 
  Users, 
  DollarSign,
  CheckCircle,
  PenTool,
  AlertCircle,
  Loader2,
  Percent
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SignaturePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [quoteData, setQuoteData] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signedInfo, setSignedInfo] = useState(null);
  const [signedBy, setSignedBy] = useState('');
  const [signatureComplete, setSignatureComplete] = useState(false);
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchQuoteData();
  }, [token]);

  useEffect(() => {
    if (canvasRef.current && !alreadySigned) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [quoteData, alreadySigned]);

  const fetchQuoteData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/signature/quote/${token}`);
      
      if (response.data.alreadySigned) {
        setAlreadySigned(true);
        setSignedInfo({
          signedBy: response.data.signedBy,
          signedAt: response.data.signedAt,
          project: response.data.project
        });
      } else {
        setQuoteData(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load quote. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!signedBy.trim()) {
      setError('Vul uw volledige naam in');
      return;
    }
    
    if (!hasSignature) {
      setError('Teken uw handtekening');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      
      await axios.post(`${API_URL}/api/signature/sign/${token}`, {
        signedBy: signedBy.trim(),
        signatureData
      });
      
      setSignatureComplete(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kan handtekening niet verzenden');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Offerte laden...</p>
        </div>
      </div>
    );
  }

  if (error && !quoteData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Kan offerte niet laden</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (alreadySigned || signatureComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">Offerte Goedgekeurd!</h2>
            <p className="text-gray-600 mb-4">
              {signatureComplete 
                ? 'Bedankt voor het ondertekenen. Uw offerte is bevestigd.'
                : 'Deze offerte is al ondertekend en goedgekeurd.'
              }
            </p>
            <div className="bg-green-50 rounded-lg p-4 text-left">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Ondertekend door:</span> {signatureComplete ? signedBy : signedInfo?.signedBy}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Datum:</span> {new Date(signatureComplete ? new Date() : signedInfo?.signedAt).toLocaleDateString('nl-NL')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { project, equipment, crew } = quoteData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-xl font-bold text-white">VS</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Van Vessem Sound & Lights</h1>
          <p className="text-gray-500">Offerte Goedkeuring</p>
        </div>

        {/* Quote Details */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Offerte Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{project.name}</h3>
                <p className="text-gray-600">{project.customer}</p>
              </div>
              <div className="text-right">
                <Badge className="bg-blue-100 text-blue-800 text-lg px-4 py-1">
                  €{project.totalValue?.toLocaleString()}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                <span className="text-sm">{project.startDate} - {project.endDate}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                <span className="text-sm">{project.location}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <span className="text-sm font-medium">{project.days} dagen</span>
              </div>
            </div>

            {/* Equipment */}
            {equipment.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 flex items-center mb-2">
                  <Package className="w-4 h-4 mr-2 text-blue-600" />
                  Apparatuur ({equipment.length} items)
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {equipment.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.name} x{item.quantity}</span>
                      <span className="text-gray-600">€{item.totalPrice}/dag</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Crew */}
            {crew.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 flex items-center mb-2">
                  <Users className="w-4 h-4 mr-2 text-purple-600" />
                  Personeel ({crew.length} leden)
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  {crew.map((member, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{member.name} - {member.role}</span>
                      <span className="text-gray-600">€{member.ratePerDay}/dag</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="border-t pt-4 mt-4 space-y-2">
              {/* Show subtotal and staffel discount if applicable */}
              {project.staffelDiscount > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Subtotaal</span>
                    <span className="text-gray-700">
                      €{project.subtotal?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-green-50 -mx-6 px-6 py-2">
                    <span className="text-green-700 flex items-center">
                      <Percent className="w-4 h-4 mr-1" />
                      Staffelkorting ({project.staffelDiscount}%)
                      {project.staffelDetails && project.staffelDetails.length > 0 && (
                        <span className="ml-2 text-xs text-green-600">
                          ({project.staffelDetails[0]?.name})
                        </span>
                      )}
                    </span>
                    <span className="font-semibold text-green-600">
                      - €{project.discountAmount?.toLocaleString()}
                    </span>
                  </div>
                </>
              )}
              
              {/* Totaal excl BTW */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Totaal excl. BTW</span>
                <span className="text-gray-900 font-medium">
                  €{project.totalValue?.toLocaleString()}
                </span>
              </div>
              
              {/* BTW 21% */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">BTW (21%)</span>
                <span className="text-gray-700">
                  €{(project.totalValue * 0.21)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              
              {/* Totaal incl BTW */}
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="font-bold text-gray-900 flex items-center">
                  <DollarSign className="w-4 h-4 mr-1" />
                  Totaal incl. BTW
                </span>
                <span className="text-2xl font-bold text-green-600">
                  €{(project.totalValue * 1.21)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              
              {project.staffelDiscount > 0 && (
                <p className="text-xs text-gray-500 mt-2 text-right">
                  U bespaart €{project.discountAmount?.toLocaleString()} dankzij onze staffelkorting!
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="shadow-lg border-blue-200">
          <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center">
              <PenTool className="w-5 h-5 mr-2 text-green-600" />
              Onderteken om Goed te Keuren
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="signedBy">Volledige Naam *</Label>
                <Input
                  id="signedBy"
                  placeholder="Vul uw volledige naam in"
                  value={signedBy}
                  onChange={(e) => setSignedBy(e.target.value)}
                  className="mt-1"
                  data-testid="signature-name-input"
                />
              </div>

              <div>
                <Label>Handtekening *</Label>
                <p className="text-sm text-gray-500 mb-2">Teken uw handtekening in het vak hieronder</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2"
                >
                  Handtekening Wissen
                </Button>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                <p>Door hierboven te ondertekenen, gaat u akkoord met de voorwaarden van deze offerte en geeft u Van Vessem Sound & Lights toestemming om door te gaan met de beschreven diensten.</p>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting || !signedBy || !hasSignature}
                className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg"
                data-testid="submit-signature-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Onderteken & Keur Goed
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignaturePage;
